import type { AllergyModel } from '@ctw/shared/api/fhir/models/allergies';
import type { BinaryModel } from '@ctw/shared/api/fhir/models/binary';
import type { CareTeamPractitionerModel } from '@ctw/shared/api/fhir/models/careteam-practitioner';
import type { ClaimModel } from '@ctw/shared/api/fhir/models/claim';
import type { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import type { CoverageModel } from '@ctw/shared/api/fhir/models/coverage';
import type { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { EncounterModel } from '@ctw/shared/api/fhir/models/encounter';
import type { EpisodeOfCareModel } from '@ctw/shared/api/fhir/models/episode-of-care';
import type { ImmunizationModel } from '@ctw/shared/api/fhir/models/immunization';
import type { MedicationStatementModel } from '@ctw/shared/api/fhir/models/medication-statement';
import type { ObservationModel } from '@ctw/shared/api/fhir/models/observation';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import type { ProcedureModel } from '@ctw/shared/api/fhir/models/procedure';
import type { ReferralModel } from '@ctw/shared/api/fhir/models/referral';
import type { RiskDiagnosisModel } from '@ctw/shared/api/fhir/models/risk-diagnosis';
import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import type { ResourceTypeString } from '@ctw/shared/api/fhir/types';
import type { Resource } from 'fhir/r4';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EPHEMERAL_UPID_SCOPED_QUALIFIED_FACTOR_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i;
export const isValidResourceId = (resourceId: string) =>
  UUID_REGEX.test(resourceId) || EPHEMERAL_UPID_SCOPED_QUALIFIED_FACTOR_REGEX.test(resourceId);

export interface ResourceTypeModelMap {
  // By convention, these models map to the underlying FHIR resource type,
  // but their actual field names are arbitrary.
  Claim: ClaimModel;
  ExplanationOfBenefit: RiskDiagnosisModel;
  Condition: ConditionModel;
  Immunization: ImmunizationModel;
  AllergyIntolerance: AllergyModel;
  Encounter: EncounterModel;
  DiagnosticReport: DiagnosticReportModel;
  ServiceRequest: ReferralModel;
  MedicationStatement: MedicationStatementModel;
  Practitioner: CareTeamPractitionerModel;
  DocumentReference: DocumentModel;
  Binary: BinaryModel;
  MeasureReport: RiskGapModel;
  Observation: ObservationModel;
  EpisodeOfCare: EpisodeOfCareModel;
  Patient: PatientModel;
  Coverage: CoverageModel;
  Procedure: ProcedureModel;
}

export const fetchableResourceTypes: Array<keyof ResourceTypeModelMap> = [
  'Claim',
  'ExplanationOfBenefit',
  'Condition',
  'Immunization',
  'AllergyIntolerance',
  'Encounter',
  'DiagnosticReport',
  'ServiceRequest',
  'MedicationStatement',
  'Practitioner',
  'DocumentReference',
  'Binary',
  'MeasureReport',
  'Observation',
  'EpisodeOfCare',
  'Patient',
  'Coverage',
  'Procedure',
] as const;

export const isFetchableResourceType = (
  resourceType: string,
): resourceType is FetchableResourceType => fetchableResourceTypes.includes(resourceType as never);

export type FetchableResourceType = keyof ResourceTypeModelMap;

export type FetchableModelType<TResourceType extends keyof ResourceTypeModelMap> =
  ResourceTypeModelMap[TResourceType];

// Resource types FQS can serve from hotstorage, and so the only types the BFF's hotload endpoint can
// report as loaded. The `X-Use-Hotstorage` header (see `FQS_HOTSTORAGE_REQUEST_OPTIONS`) is sent on
// every FQS request for these types. A `HotloadGate` / `useHotloadReady` waiting on any other
// resource type would wait on data that hotstorage never populates.
export const hotEligibleResourceTypes = [
  'Binary',
  'DocumentReference',
  'DiagnosticReport',
  'Observation',
  'Encounter',
  'EpisodeOfCare',
  'Condition',
  'AllergyIntolerance',
  'Immunization',
  'MedicationStatement',
  'MedicationAdministration',
  'MedicationDispense',
  'MedicationRequest',
  'Coverage',
] as const satisfies ReadonlyArray<ResourceTypeString>;

export const isHotEligibleResourceType = (
  resourceType: string,
): resourceType is HotEligibleResourceType =>
  hotEligibleResourceTypes.includes(resourceType as never);

export type HotEligibleResourceType = (typeof hotEligibleResourceTypes)[number];

export const isFhirResource = (maybeResource: object): maybeResource is Resource => {
  return (
    (maybeResource as Record<string, unknown>).resourceType !== undefined &&
    (maybeResource as Record<string, unknown>).id !== undefined
  );
};
